import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Download, Loader2 } from "lucide-react";
import { useProposalDetail } from "@/hooks/use-proposals";
import { formatCurrency, formatDateTime } from "@/lib/i18n";

function extractParagraphs(content: Record<string, unknown>): string[] {
  const blocks = content.blocks;
  if (!Array.isArray(blocks)) {
    return [];
  }

  return blocks
    .map((block) => {
      if (!block || typeof block !== "object") {
        return null;
      }

      const text = (block as { text?: unknown }).text;
      if (typeof text !== "string") {
        return null;
      }

      const trimmed = text.trim();
      return trimmed || null;
    })
    .filter((value): value is string => value !== null);
}

export default function ProposalPrintPage() {
  const [searchParams] = useSearchParams();
  const { proposalId } = useParams();
  const { proposal, isLoading } = useProposalDetail(proposalId);

  useEffect(() => {
    if (searchParams.get("autoprint") === "1" && proposal) {
      const timeout = setTimeout(() => {
        window.print();
      }, 120);

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [proposal, searchParams]);

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-white text-slate-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Preparing proposal print view…
      </main>
    );
  }

  if (!proposal) {
    return (
      <main className="mx-auto min-h-screen max-w-3xl bg-white px-6 py-16">
        <h1 className="text-2xl font-semibold text-slate-900">Proposal not found</h1>
        <p className="mt-2 text-sm text-slate-600">The mounted CRM proposal seam did not return a printable draft.</p>
      </main>
    );
  }

  const signedDocumentUrl = proposal.status === "signed"
    ? `/crm/proposals/${proposal.id}/signed-document`
    : null;

  return (
    <main className="min-h-screen bg-white px-6 py-10 text-slate-900 print:px-0 print:py-0">
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6 print:hidden">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Print-optimized proposal</p>
            <h1 className="mt-2 text-3xl font-semibold">{proposal.title}</h1>
            <p className="mt-2 text-sm text-slate-600">
              Open the browser print dialog to save this layout as PDF. Signed proposals can also use the stored R2 document.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-900"
            >
              <Download className="h-4 w-4" />
              Save as PDF
            </button>
            {signedDocumentUrl ? (
              <a
                href={signedDocumentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-900"
              >
                <Download className="h-4 w-4" />
                Download Signed PDF
              </a>
            ) : null}
            <Link
              to={`/offers/${proposal.id}`}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-900"
            >
              Back to offer
            </Link>
          </div>
        </div>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="rounded-full border border-slate-300 px-3 py-1 font-medium uppercase tracking-wide text-slate-700">
              {proposal.status}
            </span>
            <span>{proposal.personName}</span>
            <span>
              {proposal.totalValue === null
                ? "Value pending"
                : formatCurrency(Math.round(Number(proposal.totalValue) * 100), proposal.currency)}
            </span>
            {proposal.validUntil ? <span>Valid until {formatDateTime(proposal.validUntil)}</span> : null}
          </div>
        </section>

        <section className="space-y-5">
          {proposal.sections.map((section) => {
            const paragraphs = extractParagraphs(section.content);
            return (
              <article key={section.id} className="break-inside-avoid rounded-2xl border border-slate-200 px-6 py-5">
                <div className="mb-3">
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{section.sectionType}</p>
                </div>
                <div className="space-y-3 text-sm leading-7 text-slate-700">
                  {paragraphs.length > 0 ? paragraphs.map((paragraph) => (
                    <p key={`${section.id}-${paragraph.slice(0, 20)}`}>{paragraph}</p>
                  )) : (
                    <p>No text blocks were saved for this section.</p>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </main>
  );
}
